import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./studentviewallmodal.css";
import updown from "../../Assets/Images/CommanImages/updown.png";

export default function StudentViewAllModal({
  isOpen, onClose, title = "View All",
  type, data = [], activeSubjects = [], formatDate,
}) {
  const overlayRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [counts, setCounts] = useState({ shown: 0, total: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // reset search whenever modal reopens or type changes
  useEffect(() => {
    setSearchTerm("");
  }, [isOpen, type]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div className="vam-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className={`vam-modal ${type === "activity" ? "vam-modal-narrow" : ""}`}>
        <div className="vam-header">
          <h2 className="vam-title">
            {title}
            {counts.total > 0 && (
              <span
                style={{
                  fontWeight: 500,
                  fontSize: "13px",
                  color: "#555",
                  marginLeft: 8,
                }}
              >
                ({counts.shown}/{counts.total})
              </span>
            )}
          </h2>
          <button className="vam-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="vam-search-wrap">
          <span className="vam-search-icon">🔍</span>
          <input
            type="text"
            className="vam-search-input"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="vam-search-clear"
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="vam-body">
          {type === "test"     && <TestTable    data={data} activeSubjects={activeSubjects} formatDate={formatDate} searchTerm={searchTerm} onCountsChange={setCounts} />}
          {type === "dpp"      && <DppTable     data={data} formatDate={formatDate} searchTerm={searchTerm} onCountsChange={setCounts} />}
          {type === "activity" && <ActivityList data={data} searchTerm={searchTerm} onCountsChange={setCounts} />}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared hooks
// ─────────────────────────────────────────────────────────────────────────────
function useSort() {
  const [sortState, setSortState] = useState({ key: null, dir: null });
  const onSort = useCallback((key) => {
    setSortState((prev) => {
      if (prev.key !== key)   return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  }, []);
  return [sortState, onSort];
}

function sortRows(rows, key, dir, getValue) {
  if (!key || !dir) return rows;
  return [...rows].sort((a, b) => {
    const av = getValue(a, key);
    const bv = getValue(b, key);
    const an = parseFloat(av), bn = parseFloat(bv);
    const numeric = !isNaN(an) && !isNaN(bn);
    const cmp = numeric ? an - bn : String(av ?? "").localeCompare(String(bv ?? ""));
    return dir === "asc" ? cmp : -cmp;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// SortableTh
// ─────────────────────────────────────────────────────────────────────────────
function SortableTh({ label, colKey, sortState, onSort }) {
  const activeDir = sortState.key === colKey ? sortState.dir : null;

  return (
    <th>
      <div className="vam-th-top">
        <span className="vam-th-label">{label}</span>
        <img
          src={updown}
          alt="sort"
          style={{
            height: "20px",
            width: "20px",
            marginLeft: "5px",
            cursor: "pointer",
            flexShrink: 0,
            opacity: activeDir ? 1 : 0.4,
            filter: activeDir
              ? "invert(30%) sepia(80%) saturate(400%) hue-rotate(200deg)"
              : "none",
          }}
          onClick={() => onSort(colKey)}
          title={
            activeDir === "asc"  ? "Sorted Low→High / A→Z" :
            activeDir === "desc" ? "Sorted High→Low / Z→A" :
            "Click to sort"
          }
        />
      </div>
    </th>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TestTable
// ─────────────────────────────────────────────────────────────────────────────
function TestTable({ data = [], activeSubjects = [], formatDate, searchTerm = "", onCountsChange }) {
  const [sortState, onSort] = useSort();

  const fmt = formatDate ?? ((v) =>
    v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"
  );

  const getValue = (row, key) => {
    if (key === "test_name")   return row.test_name;
    if (key === "ts_name")     return row.ts_name;
    if (key === "total_marks") return row.Overall_total_marks;
    if (key === "test_date")   return row.test_date ? new Date(row.test_date).getTime() : 0;
    if (key === "rank")        return row.Overall_rank;
    // subject columns — key is "subj_Physics", actual field is "Physics_total_marks"
    if (key.startsWith("subj_")) {
      const subjectName = key.replace("subj_", "");
      return row[`${subjectName}_total_marks`];
    }
    return null;
  };

  // filter by search term across test name, test series, subject marks, total marks, date, rank
  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data;

    return data.filter((row) => {
      const fields = [
        row.test_name,
        row.ts_name,
        row.Overall_total_marks,
        row.Overall_rank,
        fmt(row.test_date),
        ...activeSubjects.map((s) => row[`${s}_total_marks`]),
      ];
      return fields.some(
        (f) => f !== null && f !== undefined && String(f).toLowerCase().includes(term)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm, activeSubjects]);

  // report shown/total counts up to the modal header
  useEffect(() => {
    onCountsChange?.({ shown: filteredData.length, total: data.length });
  }, [filteredData.length, data.length, onCountsChange]);

  if (data.length === 0) return <p className="vam-empty">No test data available.</p>;
  if (filteredData.length === 0) return <p className="vam-empty">No matching results found.</p>;

  const rows = sortRows(filteredData, sortState.key, sortState.dir, getValue);
  const commonProps = { sortState, onSort };

  return (
    <div className="vam-table-wrap">
      <table className="vam-table">
        <colgroup>
          <col style={{ width: "25%" }} />
          <col style={{ width: "13%" }} />
          {activeSubjects.map((s) => (
            <col key={s} style={{ width: `${45 / activeSubjects.length}%` }} />
          ))}
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>
        <thead>
          <tr>
            <SortableTh label="Test Name"   colKey="test_name"   {...commonProps} />
            <SortableTh label="Test Series" colKey="ts_name"     {...commonProps} />
            {activeSubjects.map((s) => (
              <SortableTh key={s} label={s} colKey={`subj_${s}`} {...commonProps} />
            ))}
            <SortableTh label="Total Marks" colKey="total_marks" {...commonProps} />
            <SortableTh label="Test Date"   colKey="test_date"   {...commonProps} />
            <SortableTh label="Rank"        colKey="rank"        {...commonProps} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td className="vam-name-cell" title={row.test_name ?? ""}>{row.test_name ?? "—"}</td>
              <td>{row.ts_name ?? "—"}</td>
              {activeSubjects.map((s) => {
                const val = row[`${s}_total_marks`];
                return <td key={s}>{val !== null && val !== undefined ? val : "—"}</td>;
              })}
              <td>{row.Overall_total_marks ?? "—"}</td>
              <td>{fmt(row.test_date)}</td>
              <td>{row.Overall_rank ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DppTable
// ─────────────────────────────────────────────────────────────────────────────
const EXCLUDED_COLS = ["Photo", "Student ID", "Gender ID", "Student Name", "Roll Number", "Batch Name"];
const DATE_ONLY_COLS = ["DPP Window Start", "Actual Start Time"];

function DppTable({ data = [], formatDate, searchTerm = "", onCountsChange }) {
  const [sortState, onSort] = useSort();

  const fmt = formatDate ?? ((v) => {
    if (!v || v === "—") return v;
    const d = new Date(v);
    if (isNaN(d)) return v;
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  });

  // compute these unconditionally, before any early return
  const headerRow = data[0] ?? [];
  const dataRows  = data.slice(1);

  const allHeaders = headerRow.map((item, idx) => ({
    label: item.slice(2),
    key:   item.slice(2).toLowerCase().replace(/ /g, "_"),
    originalIndex: idx,
  }));
  const filteredHeaders = allHeaders.filter((h) => !EXCLUDED_COLS.includes(h.label));

  const getCellValue = (row, h) => {
    let val = row[h.originalIndex];
    if (Array.isArray(val)) val = val.join(", ");
    val = val ?? "—";
    if (DATE_ONLY_COLS.includes(h.label)) val = fmt(val);
    return val;
  };

  const getValue = (row, key) => {
    const h = filteredHeaders.find((hh) => hh.key === key);
    return h ? getCellValue(row, h) : "";
  };

  // hook now runs on every render, regardless of data.length
  const searchedRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return dataRows;

    return dataRows.filter((row) =>
      filteredHeaders.some((h) => {
        const val = getCellValue(row, h);
        return val !== null && val !== undefined && String(val).toLowerCase().includes(term);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRows, searchTerm, filteredHeaders]);

  // report shown/total counts up to the modal header
  useEffect(() => {
    onCountsChange?.({ shown: searchedRows.length, total: dataRows.length });
  }, [searchedRows.length, dataRows.length, onCountsChange]);

  // ALL early returns now happen after every hook has been called
  if (data.length === 0) return <p className="vam-empty">No DPP data available.</p>;
  if (dataRows.length === 0) return <p className="vam-empty">No DPP data available.</p>;
  if (searchedRows.length === 0) return <p className="vam-empty">No matching results found.</p>;

  const rows = sortRows(searchedRows, sortState.key, sortState.dir, getValue);
  const commonProps = { sortState, onSort };

  const nameColWidth = 22;
  const otherColCount = Math.max(filteredHeaders.length - 1, 1);
  const otherColWidth = (100 - nameColWidth) / otherColCount;

  return (
    <div className="vam-table-wrap">
      <table className="vam-table">
        <colgroup>
          {filteredHeaders.map((h) => (
            <col
              key={h.key}
              style={{ width: `${h.label === "DPP Name" ? nameColWidth : otherColWidth}%` }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {filteredHeaders.map((h) => (
              <SortableTh key={h.key} label={h.label} colKey={h.key} {...commonProps} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {filteredHeaders.map((h) => {
                const val = getCellValue(row, h);
                const isName = h.label === "DPP Name";
                return (
                  <td key={h.key} className={isName ? "vam-name-cell" : ""} title={isName ? val : undefined}>
                    {val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ActivityList
// ─────────────────────────────────────────────────────────────────────────────
const ACTIVITY_ICONS = {
  dpp:     { bg: "#eef2ff", icon: "📄" },
  test:    { bg: "#fff3e0", icon: "📝" },
  fee:     { bg: "#e8f5e9", icon: "💳" },
  absent:  { bg: "#fff8e1", icon: "⚠️" },
  default: { bg: "#f3f4f6", icon: "📌" },
};

function getActivityStyle(label = "") {
  const l = label.toLowerCase();
  if (l.includes("dpp"))    return ACTIVITY_ICONS.dpp;
  if (l.includes("test"))   return ACTIVITY_ICONS.test;
  if (l.includes("fee"))    return ACTIVITY_ICONS.fee;
  if (l.includes("absent")) return ACTIVITY_ICONS.absent;
  return ACTIVITY_ICONS.default;
}

function ActivityList({ data = [], searchTerm = "", onCountsChange }) {
  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data;

    return data.filter((item) => {
      const fields = [
        item.label,
        item.activity_type,
        item.subject,
        item.display_date,
      ];
      return fields.some(
        (f) => f !== null && f !== undefined && String(f).toLowerCase().includes(term)
      );
    });
  }, [data, searchTerm]);

  // report shown/total counts up to the modal header
  useEffect(() => {
    onCountsChange?.({ shown: filteredData.length, total: data.length });
  }, [filteredData.length, data.length, onCountsChange]);

  if (data.length === 0) return <p className="vam-empty">No recent activity.</p>;
  if (filteredData.length === 0) return <p className="vam-empty">No matching results found.</p>;

  return (
    <ul className="vam-activity-list">
      {filteredData.map((item, i) => {
        const { bg, icon } = getActivityStyle(item.label ?? item.activity_type ?? "");
        const dateStr = item.date
          ? new Date(item.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
          : item.display_date ?? "";
        return (
          <li key={i} className="vam-activity-item">
            <span className="vam-act-icon" style={{ background: bg }}>{icon}</span>
            <div className="vam-act-info">
              <span className="vam-act-label">{item.label ?? item.activity_type ?? "Activity"}</span>
              {item.subject && <span className="vam-act-sub">{item.subject}</span>}
            </div>
            <span className="vam-act-date">{dateStr}</span>
          </li>
        );
      })}
    </ul>
  );
}